import '@xyflow/react/dist/style.css'

import { AnyObject } from '@lighthouse/core'
import { Background, MiniMap, ReactFlow } from '@xyflow/react'
import React, { useMemo, useRef } from 'react'
import styled, { createGlobalStyle, css } from 'styled-components'

import { Controls } from '@/components/FlowControls'
import { FlowDetailShape, useFlow, useFlowContainerContext } from '@/contexts/FlowContext'

import HelpLine from '../../HelpLine'
import { useNodesInteractions } from '../../hook/useNodesInteractions'
import CommonEdge from '../Edges/CommonEdge'
import ActionNode from '../Nodes/ActionNode'
import ConditionNode from '../Nodes/ConditionNode'
import EndNode from '../Nodes/EndNode'
import TriggerNode from '../Nodes/TriggerNode'
import { FlowEvent } from './FlowEvent'
import { LeftActionPanel } from './LeftActionPanel'
import { RightActionPanel } from './RightActionPanel'

interface FlowProps {
    styles?: { wrapper?: React.CSSProperties }
}

const SCxFlowEditorWrapper = styled.div`
    width: 100%;
`

const FlowEditorGlobalStyle = createGlobalStyle<{ readonly?: boolean }>`
    .react-flow {

        &.react-flow__pane_pan_start {
            .react-flow__pane {
                cursor: grab !important;
            }
        }
        .react-flow__pane.dragging {
            cursor: grabbing;
        }

        .react-flow__handle {
            background: transparent;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border-color: transparent;

            &:hover iconpark-icon{
                transform: scale(1.2);
            }

            ${({ readonly }) =>
                !readonly &&
                css`
                    &.connectingto:before {
                        content: '';
                        position: absolute;
                        display: block;
                        width: 2px;
                        height: 9px;
                        background-color: var(--color-main);
                    }

                    &::after {
                        content: '';
                        position: absolute;
                        display: block;
                        /* width: 2px;
                height: 8px; */
                        width: 20px;
                        height: 20px;
                        z-index: 10;
                        opacity: 0;
                        /* background-color: var(--color-main); */
                    }
                `}
        }

        .react-flow__node.selected {
            .react-flow__handle.connectablestart::before {
                background-color: transparent;
            }
        }
        .react-flow__handle-right {
            right: 0;
            top: 27px;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .react-flow__handle-left {
            left: 0;
            top: 27px;
            display: flex;
            justify-content: center;
            align-items: center;
        }
        .react-flow__panel {
            margin: 12px;
        }
    }
`

const nodeTypes: AnyObject = {
    TRIGGER: TriggerNode,
    ACTION: ActionNode,
    CONDITION: ConditionNode,
    END: EndNode
}

const edgeTypes: AnyObject = {
    common: CommonEdge
    // conditionSource: ConditionSourceEdge,
    // conditionInner: ConditionInnerEdge,
    // conditionTarget: ConditionTargetEdge
}

export const Flow: React.FC<FlowProps> = ({ styles }) => {
    const flowRef = useRef<HTMLDivElement>(null)
    const { log, shape, scale } = useFlowContainerContext()
    const readonly = !!log
    const { nodes, edges } = useFlow()

    const reactFlowWrapper = useRef<HTMLDivElement>(null)

    // const { handleEdgesChange } = useEdgesInteractions()
    const {
        handleNodeClick,
        handleEdgeClick,
        handleNodesChange,
        handleNodeDragStart,
        handleNodeDrag,
        handleNodeDragStop,
        handleNodeEnter,
        handleNodeLeave,
        handleNodeConnectStart,
        handleNodeConnectEnd,
        handleNodeConnect
    } = useNodesInteractions()

    const fitViewOptions = useMemo(() => {
        if (shape === FlowDetailShape.BOX) {
            return {
                padding: 0,
                minZoom: scale ?? 0.5,
                maxZoom: scale ?? 0.5
            }
        }
    }, [scale, shape])

    const wrapperStyle = styles?.wrapper ?? { height: '100%' }

    return (
        <SCxFlowEditorWrapper ref={reactFlowWrapper} style={wrapperStyle}>
            <FlowEditorGlobalStyle readonly={readonly} />
            <FlowEvent />
            <ReactFlow
                fitView
                ref={flowRef}
                deleteKeyCode={null}
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                proOptions={{ hideAttribution: true }}
                fitViewOptions={fitViewOptions}
                onNodesChange={handleNodesChange}
                // onEdgesChange={handleEdgesChange}
                // onNodeClick={handleNodeClick}
                // onEdgeClick={handleEdgeClick}
                // elementsSelectable={false}
                onConnectStart={handleNodeConnectStart}
                onConnectEnd={handleNodeConnectEnd}
                onConnect={handleNodeConnect}
                onNodeDragStart={handleNodeDragStart}
                onNodeDrag={handleNodeDrag}
                onNodeDragStop={handleNodeDragStop}
                onNodeMouseEnter={handleNodeEnter}
                onNodeMouseLeave={handleNodeLeave}
                nodesDraggable={!readonly}
                nodesConnectable={!readonly}
                maxZoom={1}
            >
                {!readonly && shape !== FlowDetailShape.BOX && (
                    <>
                        <Controls />
                        <MiniMap pannable style={{ bottom: 40 }} position="bottom-left" nodeColor="var(--color-main)" />
                    </>
                )}
                {shape !== FlowDetailShape.BOX && (
                    <>
                        <LeftActionPanel />
                        <RightActionPanel />
                    </>
                )}
                <Background style={{ background: 'var(--color-gray-100)' }} />
                <HelpLine />
            </ReactFlow>
        </SCxFlowEditorWrapper>
    )
}
